<template>
    <pendo-multiselect
        ref="multiselect"
        class="target-multiselect-filter"
        :value="value"
        :popper-options="popperOptions"
        :options="options"
        :placeholder="placeholder"
        :label-key="labelKey"
        :append-to-body="false"
        :searchable="searchable"
        :aria-label="ariaLabel"
        :close-on-select="false"
        :show-selected-values="false"
        :min-menu-width="240"
        full-width
        multiple
        @input="$emit('input', $event)"
        @leave-upper-bound="onLeaveUpperBound">
        <template #selectedLabel="{ option, selectedLabel }">
            {{ createFilterLabel(option, selectedLabel) }}
        </template>
        <template #header="{ handleKeydown }">
            <pendo-button
                v-if="value"
                ref="actionButton"
                data-cy="data-explorer-clear-select-all-button"
                class="options-header-action"
                size="mini"
                tabindex="-1"
                theme="app"
                type="subtle"
                :label="actionButtonText"
                @click="onActionButtonClick"
                @keydown="handleKeydown($event, actionButtonKeydownOverrides())" />
        </template>
        <template #option="{ option, index }">
            <pendo-option-insert
                show-checkbox
                :option="option"
                :index="index" />
        </template>
    </pendo-multiselect>
</template>

<script>
import { PendoButton, PendoMultiselect, PendoOptionInsert, PendoMultiselectHelpers } from '@pendo/components';
const { actionButtonKeyOverride } = PendoMultiselectHelpers;
import useAriaLiveRegistry from '@/common/use/use-aria-live-registry/use-aria-live-registry';

export default {
    name: 'TargetSelectorBladeMultiselectFilter',
    components: {
        PendoMultiselect,
        PendoOptionInsert,
        PendoButton,
    },
    props: {
        value: {
            type: Array,
            required: true,
        },
        options: {
            type: Array,
            default: () => [],
        },
        placeholder: {
            type: String,
            default: 'Select an option',
        },
        labelKey: {
            type: String,
            default: 'label',
        },
        multipleSelectionLabel: {
            type: String,
            default: 'Options',
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        ariaLabel: {
            type: String,
            default: '',
        },
    },
    emits: ['input'],
    setup () {
        const { queueMessage } = useAriaLiveRegistry();

        return { queueMessage };
    },
    data () {
        return {
            popperOptions: {
                modifiers: {
                    preventOverflow: {
                        boundariesElement: 'window',
                    },
                },
            },
        };
    },
    computed: {
        actionButtonText () {
            if (this.value.length === 0) {
                return this.$t('targetSelectorBladeMultiselectFilter.Select all');
            }

            return this.$t('targetSelectorBladeMultiselectFilter.Clear all');
        },
    },
    methods: {
        createFilterLabel (options, placeholder) {
            if (options.length > 1) {
                return `${this.multipleSelectionLabel} (${options.length})`;
            }
            if (options.length === 1) {
                return options[0][this.labelKey];
            }

            return placeholder;
        },
        actionButtonKeydownOverrides () {
            return {
                ...actionButtonKeyOverride(this.onActionButtonClick),
                ArrowDown: () => this.$refs.multiselect.focusTrigger(),
                PageDown: () => this.$refs.multiselect.focusTrigger(),
                End: () => this.$refs.multiselect.focusTrigger(),
            };
        },
        onActionButtonClick () {
            if (this.value.length === 0) {
                const optionCount = this.options?.length || 0;
                this.$emit('input', this.options);

                this.queueMessage({ message: this.$t('targetSelectorBladeMultiselectFilter.All {optionCount} {optionCount, plural, one {option} other {options}} selected', { optionCount }) });
            } else {
                const previouslySelected = this.value.length;
                this.$emit('input', []);

                this.queueMessage({
                    message: this.$t(
                        'targetSelectorBladeMultiselectFilter.Cleared all {previouslySelected} {previouslySelected, plural, one {item} other {items}}',
                        { previouslySelected },
                    ),
                });
            }
        },
        onLeaveUpperBound () {
            if (this.$refs.actionButton) {
                this.$refs.multiselect.pointerSet(-1);
                this.$refs.actionButton.$el.focus();
            }
        },
    },
};
</script>

<style lang="less" scoped>
.target-multiselect-filter {
    .options-header-action {
        margin: 4px 16px;
    }

    :deep(.pendo-multiselect__content-wrapper) {
        padding-top: 0;
    }
}
</style>
